import * as React from 'react'
import {observer} from 'mobx-react-lite'
import {TextValidator, ValidatorComponentProps} from 'react-material-ui-form-validator'
import {InputAdornment, TextField as TextFieldMui, TextFieldProps as TextFieldMuiProps} from '@mui/material'
import {DataCyProps} from '../dataCyProps'

export enum OcTextFieldLineType {
    Single,
    Multi,
    Area
}

type ValidatedTextFieldProps = ValidatorComponentProps &
    Omit<TextFieldMuiProps, 'name' | 'value' | 'ref' | 'rows' | 'maxRows'> &
    TextFieldProps

interface TextFieldBasicProps extends DataCyProps {
    mandatory?: boolean
    startIcon?: JSX.Element
}

interface TextFieldSingleProps extends TextFieldBasicProps {
    lineType: OcTextFieldLineType.Single
    rows?: undefined
    maxRows?: undefined
    minRows?: undefined
    multiline?: undefined
}
interface TextFieldMultiProps extends TextFieldBasicProps {
    lineType: OcTextFieldLineType.Multi
    rows?: undefined
    maxRows?: number
    minRows?: number
    multiline?: undefined
}
interface TextFieldAreaProps extends TextFieldBasicProps {
    lineType: OcTextFieldLineType.Area
    rows?: number
    maxRows?: number
    minRows?: number
    multiline?: undefined
}
interface TextFieldCustomProps extends TextFieldBasicProps {
    lineType?: undefined
    rows?: number
    maxRows?: number
    minRows?: number
    multiline?: boolean
}

type TextFieldProps = TextFieldSingleProps | TextFieldMultiProps | TextFieldAreaProps | TextFieldCustomProps

function getProps<T extends TextFieldProps & Omit<TextFieldMuiProps, 'rows' | 'maxRows' | 'minRows'>>(props: T) {
    const newProps: T = {...props}

    if (props.mandatory === true) {
        newProps.label = `${props.label} *`
    }

    newProps.InputProps = {
        ...props.InputProps
    }

    if (props.startIcon) {
        newProps.InputProps.startAdornment = <InputAdornment position="start">{props.startIcon}</InputAdornment>
    }

    if (props.lineType === OcTextFieldLineType.Single) {
        newProps.InputProps.multiline = false
        newProps.InputProps.maxRows = 1
        newProps.InputProps.minRows = 1
    }

    if (props.lineType === OcTextFieldLineType.Multi || props.lineType === OcTextFieldLineType.Area) {
        newProps.InputProps.multiline = true
        newProps.InputProps.maxRows = newProps.minRows ? newProps.minRows : 1
        newProps.InputProps.maxRows = newProps.maxRows ? newProps.maxRows : 4
        newProps.InputProps.style = {...newProps.style, padding: '13px'}

        if (props.lineType === OcTextFieldLineType.Multi) {
            newProps.InputProps.onKeyDown = e => {
                if (e.key === 'Enter') {
                    e.preventDefault()
                }
            }

            newProps.onChange = e => {
                const originOnChange = props?.onChange
                if (originOnChange) {
                    if (typeof e.target.value === 'string') {
                        e.target.value = e.target.value.replace('\u000D', '')
                    }
                    originOnChange(e)
                }
            }
        }
    }

    delete newProps.mandatory
    delete newProps.startIcon
    delete newProps.lineType
    return newProps
}

function _ValidatedTextField(props: ValidatedTextFieldProps) {
    const basicProps = React.useMemo<ValidatorComponentProps>(() => {
        return getProps<ValidatedTextFieldProps>(props)
    }, [props])

    const helperText = React.useMemo(() => {
        if (!props.helperText) {
            return '\xa0'
        }
        return props.helperText
    }, [props.helperText])

    return <TextValidator {...basicProps} fullWidth={true} variant="outlined" helperText={helperText} />
}

export const OcValidatedTextField = observer(_ValidatedTextField)

function _TextField(props: TextFieldProps & Omit<TextFieldMuiProps, 'rows' | 'maxRows' | 'minRows'>) {
    const basicProps: TextFieldMuiProps = React.useMemo(() => {
        return getProps<TextFieldProps & Omit<TextFieldMuiProps, 'rows' | 'maxRows' | 'minRows'>>(props)
    }, [props])

    return <TextFieldMui {...basicProps} variant="outlined" />
}

export const OcTextField = observer(_TextField)
